import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { BarChart3, CalendarRange, Target, Users } from 'lucide-react';
import type { StrategyLevel, StrategyNode, StrategyOverview, StrategyStatus } from '@mnc/contracts';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';
import { VisibilityBadge } from '../../components/records';
import { LEVEL_TONE, ProgressBar, ProgressRing, StatusBadge, StatusDistribution, childLevelOf, descendantsOf, fmtDate, levelsBelow, useLevelLabel } from './shared';

function statusCounts(nodes: StrategyNode[]): Partial<Record<StrategyStatus, number>> {
  const out: Partial<Record<StrategyStatus, number>> = {};
  for (const n of nodes) out[n.rollup.status] = (out[n.rollup.status] ?? 0) + 1;
  return out;
}

/**
 * Visual summary of a node and its subtree (MRLCO Strategy page + consolidated report, generalised): hero facts,
 * one card per pillar (the level right below the node) with its nested detail, and a progress table at the
 * "detail level" the entity chose (initiatives or projects, depending on the configured cascade).
 */
export function OverviewTab({ data, focus, onFocus, onScope }: { data: StrategyOverview; focus: StrategyLevel | undefined; onFocus: (level: StrategyLevel) => void; onScope: (tab: 'schedule' | 'kpis', nodeId: string) => void }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const levelLabel = useLevelLabel(data.config);
  const { node, nodes, summary, config } = data;
  const pillarLevel = childLevelOf(config, node.level);
  const focusChoices = levelsBelow(config, node.level).slice(1);
  const focusLevel = focus && focusChoices.includes(focus) ? focus : (focusChoices[0] ?? pillarLevel);
  const pillars = useMemo(() => nodes.filter((n) => n.parent_id === node.id), [nodes, node.id]);
  const focusNodes = useMemo(() => (focusLevel ? nodes.filter((n) => n.level === focusLevel).sort((a, b) => (a.rollup.progress ?? -1) - (b.rollup.progress ?? -1) || a.code.localeCompare(b.code)) : []), [nodes, focusLevel]);
  const pillarOf = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    return (n: StrategyNode): StrategyNode | null => {
      let cur: StrategyNode | undefined = n;
      while (cur && cur.parent_id && cur.parent_id !== node.id) cur = byId.get(cur.parent_id);
      return cur && cur.parent_id === node.id ? cur : null;
    };
  }, [nodes, node.id]);
  const open = (id: string) => void navigate({ to: '/strategy/nodes/$id', params: { id } });
  const kpiOnTrack = summary.kpis.on_track + summary.kpis.achieved;

  return (
    <div className="grid gap-4">
      {/* Hero */}
      <div className="card p-5">
        <div className="flex flex-wrap items-start gap-5">
          <ProgressRing value={node.rollup.progress} size={96} stroke={9} className="text-lg" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', LEVEL_TONE[node.level])}>{levelLabel(node.level)}</span>
              <code className="text-xs text-neutral-500">{node.code}</code>
              <StatusBadge status={node.rollup.status} />
              <VisibilityBadge visibility={node.visibility} />
            </div>
            <h2 className="mt-1 text-xl font-semibold" dir="auto">{pick(i18n.language, node.name_en, node.name_ar)}</h2>
            {(node.description_en || node.description_ar) && <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400" dir="auto">{pick(i18n.language, node.description_en, node.description_ar)}</p>}
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {summary.by_level.filter((l) => l.level !== node.level).map((l) => (
                <button key={l.level} type="button" className={cn('rounded-full px-2.5 py-1 font-medium', LEVEL_TONE[l.level])} onClick={() => focusChoices.includes(l.level) && onFocus(l.level)}>
                  {l.total} {levelLabel(l.level, true)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4 lg:grid-cols-2">
            <Stat icon={<Target size={14} />} value={`${kpiOnTrack}/${summary.kpis.total}`} label={t('strategy.overview.kpisOnTrack')} tone={summary.kpis.behind > 0 ? 'warn' : 'ok'} sub={summary.kpis.behind > 0 ? t('strategy.overview.kpisBehind', { count: summary.kpis.behind }) : undefined} />
            <Stat icon={<BarChart3 size={14} />} value={String(summary.tasks.open)} label={t('strategy.overview.tasksOpen')} tone={summary.tasks.overdue > 0 ? 'danger' : 'ok'} sub={summary.tasks.overdue > 0 ? t('strategy.overdueCount', { count: summary.tasks.overdue }) : undefined} />
            <Stat icon={<CalendarRange size={14} />} value={summary.date_range.start ? `${fmtDate(summary.date_range.start, i18n.language)} → ${fmtDate(summary.date_range.end, i18n.language)}` : '—'} label={t('strategy.overview.dateRange')} small />
            <Stat icon={<Users size={14} />} value={String(summary.team_members)} label={t('strategy.overview.teamMembers')} sub={summary.challenges.open > 0 ? t('strategy.challengesOpenCount', { count: summary.challenges.open }) : undefined} tone={summary.challenges.high > 0 ? 'warn' : 'ok'} />
          </div>
        </div>
        {node.level === 'strategy' && (node.vision_en || node.vision_ar || node.mission_en || node.mission_ar) && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {(node.vision_en || node.vision_ar) && <div className="rounded-lg border border-teal-200 bg-teal-50 p-3 dark:border-teal-900/50 dark:bg-teal-900/20"><div className="text-[10px] font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">{t('strategy.vision')}</div><p className="mt-1 text-sm" dir="auto">{pick(i18n.language, node.vision_en, node.vision_ar)}</p></div>}
            {(node.mission_en || node.mission_ar) && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/50 dark:bg-blue-900/20"><div className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 dark:text-blue-300">{t('strategy.mission')}</div><p className="mt-1 text-sm" dir="auto">{pick(i18n.language, node.mission_en, node.mission_ar)}</p></div>}
          </div>
        )}
      </div>

      {/* Pillars */}
      {pillarLevel && (
        <section>
          <h3 className="mb-2 text-sm font-semibold text-neutral-600 dark:text-neutral-400">{levelLabel(pillarLevel, true)} <span className="font-normal text-neutral-400">({pillars.length})</span></h3>
          {pillars.length === 0 && <p className="text-sm text-neutral-500">{t('strategy.overview.noItems', { level: levelLabel(pillarLevel, true) })}</p>}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {pillars.map((p) => {
              const below = descendantsOf(nodes, p.id);
              const detail = focusLevel && focusLevel !== pillarLevel ? below.filter((n) => n.level === focusLevel) : below.filter((n) => n.parent_id === p.id);
              const deeper = levelsBelow(config, pillarLevel).filter((l) => below.some((n) => n.level === l));
              return (
                <div key={p.id} className="card flex flex-col overflow-hidden">
                  <button type="button" className={cn('p-4 text-start', LEVEL_TONE[pillarLevel].split(' ')[0], 'text-white')} style={{ backgroundColor: 'var(--color-brand-700)' }} onClick={() => open(p.id)}>
                    <div className="flex items-start gap-2">
                      <span className="font-mono text-sm font-bold opacity-90">{p.code}</span>
                      <span className="flex-1 text-sm font-semibold uppercase leading-tight" dir="auto">{pick(i18n.language, p.name_en, p.name_ar)}</span>
                      <ProgressRing value={p.rollup.progress} size={40} stroke={4} className="text-white" />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] font-semibold">
                      {deeper.map((l) => <span key={l} className="rounded-full bg-white/15 px-2 py-0.5">{p.rollup.children_by_level[l] ?? 0} {levelLabel(l, true)}</span>)}
                      {p.rollup.kpi_count > 0 && <span className="rounded-full bg-white/15 px-2 py-0.5">{t('strategy.kpiCount', { count: p.rollup.kpi_count })}</span>}
                    </div>
                  </button>
                  <div className="flex flex-1 flex-col gap-2 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadge status={p.rollup.status} />
                      <span className={cn('text-xs', p.rollup.tasks_overdue > 0 ? 'font-medium text-red-600' : 'text-neutral-500')}>{t('strategy.tasksOpenCount', { count: p.rollup.tasks_open })}{p.rollup.tasks_overdue > 0 && ` · ${t('strategy.overdueCount', { count: p.rollup.tasks_overdue })}`}</span>
                    </div>
                    {detail.length > 0 ? <StatusDistribution counts={statusCounts(detail)} /> : <p className="text-xs text-neutral-400">{t('strategy.noChildren')}</p>}
                    <ul className="divide-y divide-neutral-100 text-sm dark:divide-neutral-800">
                      {detail.slice(0, 4).map((d) => (
                        <li key={d.id}>
                          <button type="button" className="flex w-full items-center gap-2 py-1 text-start" onClick={() => open(d.id)}>
                            <code className="shrink-0 text-[11px] text-neutral-500">{d.code}</code>
                            <span className="min-w-0 flex-1 truncate" dir="auto">{pick(i18n.language, d.name_en, d.name_ar)}</span>
                            <ProgressBar value={d.rollup.progress} />
                          </button>
                        </li>
                      ))}
                      {detail.length > 4 && <li className="py-1 text-xs text-neutral-500">{t('strategy.overview.more', { count: detail.length - 4 })}</li>}
                    </ul>
                    <div className="mt-auto flex flex-wrap gap-1 pt-1">
                      <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => open(p.id)}>{t('strategy.overview.open')}</button>
                      <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => onScope('schedule', p.id)}>{t('strategy.tabs.schedule')}</button>
                      <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => onScope('kpis', p.id)}>{t('strategy.tabs.kpis')}</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Progress at the detail level */}
      {focusLevel && (
        <section className="card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2 dark:border-neutral-800">
            <h3 className="font-semibold">{t('strategy.overview.progressTitle', { level: levelLabel(focusLevel, true) })}</h3>
            {focusChoices.length > 1 && (
              <div className="flex overflow-hidden rounded-lg border border-neutral-300 text-xs dark:border-neutral-700">
                {focusChoices.map((l) => <button key={l} type="button" onClick={() => onFocus(l)} className={cn('px-2.5 py-1', focusLevel === l ? 'bg-brand-600 text-white' : 'bg-white dark:bg-neutral-900')}>{levelLabel(l, true)}</button>)}
              </div>
            )}
          </div>
          <div className="px-4 py-3">
            <StatusDistribution counts={statusCounts(focusNodes)} className="mb-3" />
            {focusNodes.length === 0 && <p className="text-sm text-neutral-500">{t('strategy.overview.noItems', { level: levelLabel(focusLevel, true) })}</p>}
            {focusNodes.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-neutral-500">
                    <tr><th className="py-1 text-start">{levelLabel(focusLevel)}</th>{pillarLevel && focusLevel !== pillarLevel && <th className="py-1 text-start">{levelLabel(pillarLevel)}</th>}<th className="py-1 text-start">{t('strategy.status')}</th><th className="py-1 text-start">{t('strategy.kpi.progress')}</th><th className="py-1 text-start">{t('tasks.pageTitle')}</th><th className="py-1 text-start">{t('strategy.endDate')}</th></tr>
                  </thead>
                  <tbody>
                    {focusNodes.map((n) => {
                      const pillar = pillarOf(n);
                      return (
                        <tr key={n.id} className="cursor-pointer border-t border-neutral-100 hover:bg-neutral-50 dark:border-neutral-800 dark:hover:bg-neutral-800/50" onClick={() => open(n.id)}>
                          <td className="py-1.5 pe-3"><code className="me-2 text-[11px] text-neutral-500">{n.code}</code><span dir="auto">{pick(i18n.language, n.name_en, n.name_ar)}</span></td>
                          {pillarLevel && focusLevel !== pillarLevel && <td className="py-1.5 pe-3 text-xs text-neutral-500" dir="auto">{pillar ? `${pillar.code} · ${pick(i18n.language, pillar.name_en, pillar.name_ar)}` : '—'}</td>}
                          <td className="py-1.5 pe-3"><StatusBadge status={n.rollup.status} /></td>
                          <td className="py-1.5 pe-3"><ProgressBar value={n.rollup.progress} /></td>
                          <td className="py-1.5 pe-3 text-xs"><span className="inline-flex items-center gap-1.5">{n.rollup.tasks_open}{n.rollup.tasks_overdue > 0 && <Badge tone="danger">{t('strategy.overdueCount', { count: n.rollup.tasks_overdue })}</Badge>}</span></td>
                          <td className="py-1.5 text-xs text-neutral-500">{fmtDate(n.end_date, i18n.language)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function Stat({ icon, value, label, sub, tone = 'ok', small }: { icon: React.ReactNode; value: string; label: string; sub?: string; tone?: 'ok' | 'warn' | 'danger'; small?: boolean }) {
  return (
    <div className={cn('rounded-lg border p-2.5', tone === 'danger' ? 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-900/20' : tone === 'warn' ? 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/20' : 'border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900/40')}>
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-neutral-500">{icon}{label}</div>
      <div className={cn('mt-0.5 font-semibold tabular-nums', small ? 'text-xs' : 'text-lg')}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-500">{sub}</div>}
    </div>
  );
}
